import * as React from "react";
import { connect } from "react-redux";
import { ApplicationState } from "../../store";
import * as SharedAccountCredentialsState from "../../store/SharedAccountCredentials";
import { PageControl } from "./PageControl";
import { debounce } from "lodash";
import { defaultPageSize } from "../../helpers";
import { SharedAccountCredentialsResponse } from "../../api/ApiClient";
import { Spinner } from "./Spinner";
import SearchInput from "./SearchInput";
import { useSnackbar } from "notistack";
import { Button } from "components/ui/button";
import { AdminTable } from "./AdminTable";

interface Injected {
  sharedAccountCredentials: SharedAccountCredentialsState.SharedAccountCredentialsState;
}

type OwnProps = {
  onSelected: (cred: SharedAccountCredentialsResponse) => any;
  pageSize?: number;
  showFirst?: number | null | undefined;
  selected?: Array<SharedAccountCredentialsResponse>;
  renderButton?: (cred: SharedAccountCredentialsResponse) => any;
  selectedLabel?: string;
  selectedBtnClass?: string;
  unselectedLabel?: string;
  unselectedBtnClass?: string;
  btnStyle?: Object;
};

interface SnackProps {
  enqueueSnackbar?: (node: React.ReactNode, options?: any) => void;
  closeSnackbar?: (key?: any) => void;
}

type Props = Injected &
  typeof SharedAccountCredentialsState.actionCreators &
  OwnProps &
  SnackProps;

interface State {
  typed: string;
}

class SelectSharedAccountCredentials extends React.Component<Props, State> {
  state: State = {
    typed: "",
  };

  componentDidMount() {
    console.log("SelectSharedCred: Requesting...");
    this.refresh();
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.pageSize !== prevProps.pageSize) {
      this.refresh();
    }
  }

  refresh = async () => {
    try {
      const { pageSize } = this.props;
      const { typed: searchTerm } = this.state;
      await this.props.setFetchParamSharedCredentials({
        page: 0,
        pageSize: pageSize || defaultPageSize,
        searchTerm,
      });
    } catch (e) {
      console.error("Error requesting shared creds", e);
      const message = (e as any)?.message || "Error requesting shared creds";
      this.props.enqueueSnackbar &&
        this.props.enqueueSnackbar(
          <span style={{ fontSize: 12 }}>{message}</span>,
          { variant: "error" },
        );
    }
  };

  handleTyped = (value: string) => {
    this.setState({ typed: value }, this.search);
  };

  search = debounce(this.refresh, 1000);

  public render() {
    const { sharedCredentials, searchTerm, isLoading } =
      this.props.sharedAccountCredentials;

    const { typed } = this.state;

    return (
      <div>
        <SearchInput
          id="typedSharedCred"
          name="typedSharedCred"
          placeholder="type here"
          value={typed}
          onChange={(e) => this.handleTyped(e.target.value)}
        />
        <h4>
          {searchTerm ? `Credentials Matching '${searchTerm}'` : "Credentials"}
          {isLoading && <Spinner />}
        </h4>
        {this.renderAccountsTable(sharedCredentials)}
      </div>
    );
  }

  private defaultRenderButton = (
    sharedAccountCredentials: SharedAccountCredentialsResponse,
  ) => {
    const {
      selected,
      onSelected,
      btnStyle,
      selectedBtnClass,
      selectedLabel,
      unselectedBtnClass,
      unselectedLabel,
    } = this.props;
    const isSelected =
      selected && selected.find((a) => a.id === sharedAccountCredentials.id);
    const className = isSelected
      ? selectedBtnClass || "btn-primary"
      : unselectedBtnClass || "btn-default";
    return (
      <Button
        className={className}
        style={btnStyle as React.CSSProperties | undefined}
        type="button"
        variant={isSelected ? "default" : "outline"}
        onClick={() => onSelected(sharedAccountCredentials)}
      >
        {!isSelected
          ? unselectedLabel || "Select"
          : selectedLabel || "Unselect"}
      </Button>
    );
  };

  private renderAccountsTable(creds: SharedAccountCredentialsResponse[]) {
    const { renderButton, showFirst } = this.props;
    const { isLoading } = this.props.sharedAccountCredentials;
    creds = !showFirst ? creds : (creds || []).slice(0, showFirst);

    return (
      <div>
        <AdminTable aria-label="Shared account credentials">
          <thead>
            <tr>
              <th className="w-[100px]" />
              <th>Id</th>
              <th>Name</th>
              <th>Description</th>
              <th>SystemType</th>
            </tr>
          </thead>
          <tbody>
            {creds.map((cred, i) => (
              <tr key={"f_" + i}>
                <td className="w-[100px]">
                  {renderButton
                    ? renderButton(cred)
                    : this.defaultRenderButton(cred)}
                </td>
                <td>{cred.id}</td>
                <td>{cred.name}</td>
                <td>{cred.description}</td>
                <td>{cred.systemType}</td>
              </tr>
            ))}
          </tbody>
        </AdminTable>
        {!showFirst && (
          <PageControl
            onNext={this.props.nextSharedCredentialsPage}
            onPrev={this.props.prevSharedCredentialsPage}
            currentPage={this.props.sharedAccountCredentials.page}
            isLoading={isLoading}
            disablePrev={this.props.sharedAccountCredentials.page === 0}
            disableNext={
              this.props.sharedAccountCredentials.sharedCredentials.length <
              this.props.sharedAccountCredentials.pageSize
            }
          />
        )}
      </div>
    );
  }
}

const ConnectedSelectSharedAccountCredentials = connect<
  any,
  any,
  OwnProps,
  ApplicationState
>(
  (state) => ({
    sharedAccountCredentials: state.sharedAccountCredentials,
  }),
  { ...SharedAccountCredentialsState.actionCreators },
)(SelectSharedAccountCredentials);

const SelectSharedAccountCredentialsWithSnackbar: React.FC<any> = (props) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  return (
    <ConnectedSelectSharedAccountCredentials
      {...props}
      enqueueSnackbar={enqueueSnackbar}
      closeSnackbar={closeSnackbar}
    />
  );
};

export default SelectSharedAccountCredentialsWithSnackbar;
